import {
  BMComponentsType,
  BMDivider,
  BMPrimitives,
  BMText,
  useBMTheme,
} from "@traveloka/district-bloom";
import { Fragment } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { StyleSheet } from "react-native";
import BMView from "../../../../../components/View/BMView";
import { useMobileContentResource } from "../../../../../core/resource/MobileResourceProvider";
import { useTravelerPickerContextV2 } from "../../../../../providers/TravelerPickerContextV2";
import CheckboxEmployee from "../CheckboxEmployee";
import { Employee } from "../types";
import { useCheckEmployeeDisabled } from "./hooks";
import { SelectEmployeeFormValues } from "./types";
import { filterEmployees, filterEmployeesByDivision } from "./utils";

const setValueConfig = {
  shouldDirty: true,
  shouldTouch: true,
};

export default function SelectEmployeeDrawerRecentPick() {
  const { recentPickEmployees } = useTravelerPickerContextV2();
  const { CorporateTravelerPicker: cr } = useMobileContentResource();
  const { control, setValue } = useFormContext<SelectEmployeeFormValues>();
  const [selectedEmployees, divisions, searchEmployee] = useWatch({
    control,
    name: ["selectedEmployees", "divisions", "searchEmployee"],
  });
  const { BMComponents } = useBMTheme();

  const isDisabled = useCheckEmployeeDisabled();

  const shownRecentPickEmployees = recentPickEmployees
    .filter(filterEmployees(searchEmployee))
    .filter(filterEmployeesByDivision(divisions));

  if (shownRecentPickEmployees.length === 0) {
    return null;
  }

  const styles = createStyles(BMComponents);

  function handleEmployeeChange(employee: Employee) {
    const isSelected = selectedEmployees.find(
      (e) => e.email === employee.email
    );

    if (isSelected) {
      return selectedEmployees.filter((e) => e.email !== employee.email);
    }

    return [...selectedEmployees, employee];
  }

  return (
    <BMView style={styles.container} spacing="lg">
      <BMText variant="uiLargeLi" ink="BMStyleTextInkSecondary">
        {cr.yourRecentPickSectionTitle}
      </BMText>
      <BMView style={styles.innerWrapper}>
        {shownRecentPickEmployees.map((employee, index) => (
          <Fragment key={index}>
            <CheckboxEmployee
              testID={`recent_pick-employee-checkbox-${index}`}
              disabled={isDisabled}
              style={styles.padding}
              employee={employee}
              searchKeyword={searchEmployee}
              value={Boolean(
                selectedEmployees.find((e) => e.email === employee.email)
              )}
              onChange={() =>
                setValue(
                  "selectedEmployees",
                  handleEmployeeChange(employee),
                  setValueConfig
                )
              }
            />
            {index < recentPickEmployees.length - 1 && (
              <BMDivider type="SUBTLE" />
            )}
          </Fragment>
        ))}
      </BMView>
    </BMView>
  );
}

function createStyles(BMComponents: BMComponentsType) {
  return StyleSheet.create({
    container: {
      marginTop: BMPrimitives.BMSize.lg,
    },
    innerWrapper: {
      borderColor: BMComponents.BMStyleLineBordercolorSubtle,
      borderWidth: BMPrimitives.BMBorder.width.thick,
      borderRadius: BMComponents.BMStyleCardRadiusCompact,
    },
    padding: {
      padding: BMPrimitives.BMSize.md,
    },
  });
}
